import type { FeatureGroup, FeatureItem } from '@/features/Features';
import { useFeaturesStore } from '@/features/Features';
import cloneDeep from 'lodash/cloneDeep';
import type {
  PkvFeatureMetaType,
  RelevantInputFields,
} from '@/features/Sparten/pkv/utils/types';
import { VERSICHERUNGSART } from '@/features/Sparten/shared/types';
import { usePkvInputStore } from '@/features/Sparten/pkv/useInputStore';

export const featureFilterFunction = (
  step: 'input' | 'comparison',
): FeatureGroup<PkvFeatureMetaType>[] => {
  const {
    versicherungsart,
    ambulant,
    stationaer,
    zahn,
    krankentagegeld,
    krankenhaustagegeld,
    pflegezusatz,
    pflegepflichtversicherung,
    auslandsreisekrankenversicherung,
  } = usePkvInputStore();

  const relevantInputFields: RelevantInputFields = {
    ambulant,
    stationaer,
    zahn,
    krankentagegeld,
    krankenhaustagegeld,
    pflegezusatz,
    pflegepflichtversicherung,
    auslandsreisekrankenversicherung,
  };

  const clonedFeatures: FeatureGroup<PkvFeatureMetaType>[] = cloneDeep(
    useFeaturesStore().data,
  ) as FeatureGroup<PkvFeatureMetaType>[];

  const relevantComparisonIdentifiers = [
    'rubrik1_T_1',
    'rubrik2_T_2',
    'rubrik3_T_51',
  ];

  // Prüft, ob eine Gruppe basierend auf der Versicherungsart sichtbar ist
  const isGroupVisible = (meta: PkvFeatureMetaType): boolean => {
    if (meta === null) return true;

    const { visibleZusatz, visibleVoll, visible } = meta;
    const isVoll = visibleVoll && versicherungsart === VERSICHERUNGSART.VOLL;
    const isZusatz =
      visibleZusatz && versicherungsart === VERSICHERUNGSART.ZUSATZ;

    if (!isVoll && !isZusatz) return false;

    if (Array.isArray(visible)) {
      return visible.some(
        (field) =>
          field === true ||
          relevantInputFields[field as keyof RelevantInputFields],
      );
    }

    return false;
  };

  // Prüft, ob ein Item basierend auf Meta-Daten und Schritt sichtbar ist
  const isItemVisible = (item: FeatureItem<PkvFeatureMetaType>): boolean => {
    if (
      step === 'comparison' &&
      relevantComparisonIdentifiers.includes(item.identifier)
    ) {
      return true;
    }

    if (item.meta) {
      const { visibleZusatz, visibleVoll } = item.meta;
      return (
        (visibleVoll && versicherungsart === VERSICHERUNGSART.VOLL) ||
        (visibleZusatz && versicherungsart === VERSICHERUNGSART.ZUSATZ)
      );
    }

    return false;
  };

  // Filtert Gruppen und Items basierend auf Sichtbarkeit
  return clonedFeatures
    .filter((group) => group.meta && isGroupVisible(group.meta))
    .map((group) => ({
      ...group,
      items: group.items.filter(isItemVisible),
    }))
    .filter((group) => group.items.length > 0);
};
